import { usePage } from '@inertiajs/react'
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import type { CollectionRecord } from './collections'

// CMS live preview for dynamic collections (iteration 0009). When the page renders
// inside the operator editor's preview (?preview=1), the editor pushes the current
// draft of a collection (testimonials / milestones / faqs) over postMessage; the
// marketing pages read it here and render it instead of the published payload.
type Collections = Record<string, CollectionRecord[]>
type PreviewProp = { active?: boolean; allowed_origins?: string[] }
const PREVIEW_SOURCE = 'g4-cms'

const Ctx = createContext<Collections>({})

export function PreviewCollectionsProvider({ children }: { children: ReactNode }) {
  const [collections, setCollections] = useState<Collections>({})

  let preview: PreviewProp = {}
  try {
    // biome-ignore lint/correctness/useHookAtTopLevel: invoked once per render; only the throwing test-mock access is guarded
    preview = (usePage().props.preview as PreviewProp | undefined) ?? {}
  } catch {
    preview = {}
  }
  const active = preview.active === true
  const allowed = useMemo(() => new Set(preview.allowed_origins ?? []), [preview.allowed_origins])

  useEffect(() => {
    if (!active || typeof window === 'undefined') return
    function onMessage(event: MessageEvent) {
      if (!allowed.has(event.origin)) return
      const data = event.data as
        | { source?: string; type?: string; key?: string; records?: CollectionRecord[] }
        | undefined
      if (!data || data.source !== PREVIEW_SOURCE || data.type !== 'preview:collection') return
      if (data.key)
        setCollections((prev) => ({ ...prev, [data.key as string]: data.records ?? [] }))
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [active, allowed])

  return <Ctx.Provider value={collections}>{children}</Ctx.Provider>
}

// Preview override for a collection, or undefined when not previewing it.
export function usePreviewCollection(key: string): CollectionRecord[] | undefined {
  return useContext(Ctx)[key]
}
